/**
 * AI Providers API client — Settings → AI Provider 탭.
 *
 * chat-server proxy: GET /api/v1/admin/ai-providers/detect
 *   (chat-server 가 backend /api/platform/ai-providers/internal/detect 로 INTERNAL_SERVICE_TOKEN
 *    forward — 자세한 사유는 chat-server 의 ai-providers.routes.ts 주석 참조.)
 *
 * 권한: ADMIN / SYSTEM_ADMIN 만 호출 가능 (chat-server route 단에서 검증).
 */

import { apiClient } from './client';

export interface EnvProvider {
  id: string;
  source: 'env';
  name: string;
  vendor: string;
  base_url: string;
  model_hint: string | null;
  api_key_preview: string;
  env_file: string;
  env_keys: string[];
  kind: string;
}

export interface CliProvider {
  id: string;
  source: 'cli';
  name: string;
  vendor: string;
  bin: string;
  path: string;
  version: string;
  model_list?: string[];
}

export interface AiProviderSelection {
  selected_id: string | null;
  model_id: string | null;
  persisted: boolean;
  scope: 'user' | 'global' | 'default';
  updated_at: string | null;
}

export interface AiProviderModel {
  id: string;
  name: string;
}

export interface AiProviderModelsResult {
  providerId: string;
  models: AiProviderModel[];
  source: 'remote' | 'static' | 'unsupported';
}

export interface AiProviderDetectResult {
  env_file: string;
  env_file_exists: boolean;
  providers: {
    env: EnvProvider[];
    cli: CliProvider[];
  };
  selection: AiProviderSelection;
}

export async function detectAiProviders(): Promise<AiProviderDetectResult> {
  const { data } = await apiClient.get<AiProviderDetectResult>('/admin/ai-providers/detect');
  // 방어 가드(additive) — providers.env/cli 가 배열이 아닌 비정형 응답에도 소비처 map 크래시 차단.
  return {
    ...data,
    providers: {
      env: Array.isArray(data?.providers?.env) ? data.providers.env : [],
      cli: Array.isArray(data?.providers?.cli) ? data.providers.cli : [],
    },
  };
}

export interface AiProviderSetSelectionResponse {
  selected_id: string | null;
  model_id: string | null;
  updated_at: string | null;
}

/**
 * active provider 변경 (admin 자신의 user-scope).
 *   - selectedId=null → 삭제 (global/default fallback)
 *   - modelId optional — null/undefined 면 env default 사용
 */
export async function setAiProviderSelection(
  selectedId: string | null,
  modelId?: string | null,
): Promise<AiProviderSetSelectionResponse> {
  const { data } = await apiClient.put<AiProviderSetSelectionResponse>(
    '/admin/ai-providers/selection',
    { selected_id: selectedId, model_id: modelId ?? null },
  );
  return data;
}

/** provider 별 사용 가능한 모델 list (env.openai 는 /v1/models 동적 조회). */
export async function listAiProviderModels(providerId: string): Promise<AiProviderModelsResult> {
  const { data } = await apiClient.get<AiProviderModelsResult>(
    '/admin/ai-providers/models',
    { params: { providerId } },
  );
  return { ...data, models: Array.isArray(data?.models) ? data.models : [] };
}

export interface AiProviderHealthResult {
  ok: boolean;
  latency_ms: number;
  detail?: string;
  status?: number;
}

/**
 * provider 연결 헬스체크 — env 는 실제 API 연결 검증(/models GET), cli 는 바이너리 가용성.
 * 실제 프롬프트 미전송(비용/로그 최소화). 최대 ~12초 소요.
 */
export async function checkAiProviderHealth(providerId: string): Promise<AiProviderHealthResult> {
  const { data } = await apiClient.get<AiProviderHealthResult>(
    '/admin/ai-providers/health-check',
    { params: { providerId }, timeout: 15_000 },
  );
  return data;
}

// ─── 오피스 편집기 전용 설정 (2026-08-15, PR4) ───────────────────────────────
//
// 워크스페이스 문서 편집기(md·Word·PDF·Excel·PPT·한글·평문) 안의 AI 만 다른 provider 로
// 뺄 수 있게 한다. 왜 따로 두는가 — 전역 선택이 CLI 면 동시성이 1이라 편집기 실행 하나가
// 채팅·회의록·아이콘 생성까지 수 분간 막는다.
//
// ⚠ 모든 필드의 `null` 은 **미설정 = 종전대로**(chat-server 의 env → 기본값)를 뜻한다.

export interface OfficeAiSettings {
  /** null 이면 전역 선택(위 AI Provider 탭에서 고른 것)을 그대로 따른다. */
  provider_id: string | null;
  model_id: string | null;
  /** 한 응답의 최대 토큰. null = 기본(8000). */
  max_tokens: number | null;
  /** 문서를 고치는 도구 사용 여부. null = 기본(사용). */
  tools_enabled: boolean | null;
  /** 도구만 끌 편집기 — 대화·초안은 그대로 된다. */
  disabled_editors: string[];
  updated_at: string | null;
}

export interface OfficeAiSettingsResult {
  settings: OfficeAiSettings;
  /** 도구를 가진 편집기 목록(서버가 준다) — 한글·평문은 애초에 도구가 없어 빠져 있다. */
  tool_editors?: string[];
  /**
   * 오피스 전용 CLI provider 의 id 접두 — 화면이 카드 id 를 만들 때 쓴다.
   *
   * `office.cli.claude` = "감지된 `cli.claude` 를 **복사한** 오피스 전용 항목". 바이너리도
   * 모델도 원본과 같고, 다른 것은 서버의 **실행 레인** 하나뿐이다. 그래서 편집기 AI 가
   * 채팅·회의록과 줄을 나눠 선다.
   *
   * ⚠ 접두를 화면에 적어 두면 서버와 갈린다 — 반드시 이 값을 쓴다.
   */
  cli_prefix?: string;
  /** 오피스 레인의 동시 실행 수 — 화면이 "줄을 따로 선다" 를 근거와 함께 보여 준다. */
  cli_lane_concurrency?: number;
}

export async function getOfficeAiSettings(): Promise<OfficeAiSettingsResult> {
  const { data } = await apiClient.get<OfficeAiSettingsResult>('/admin/ai-providers/office-settings');
  return {
    settings: {
      provider_id: data?.settings?.provider_id ?? null,
      model_id: data?.settings?.model_id ?? null,
      max_tokens: data?.settings?.max_tokens ?? null,
      tools_enabled: data?.settings?.tools_enabled ?? null,
      disabled_editors: Array.isArray(data?.settings?.disabled_editors) ? data.settings.disabled_editors : [],
      updated_at: data?.settings?.updated_at ?? null,
    },
    tool_editors: Array.isArray(data?.tool_editors) ? data.tool_editors : undefined,
    cli_prefix: typeof data?.cli_prefix === 'string' ? data.cli_prefix : undefined,
    cli_lane_concurrency: typeof data?.cli_lane_concurrency === 'number' ? data.cli_lane_concurrency : undefined,
  };
}

export async function saveOfficeAiSettings(
  patch: Omit<OfficeAiSettings, 'updated_at'>,
): Promise<OfficeAiSettingsResult> {
  const { data } = await apiClient.put<OfficeAiSettingsResult>(
    '/admin/ai-providers/office-settings',
    patch,
  );
  return data;
}

// ─── 수동 외부 API LLM 연결 (admin BFF → backend 직접) ───────────────────────
// vendor: 'anthropic'(Claude) | 'openai'(OpenAI·Codex) | 'gemini' | 'local' | 'openai-compatible' | 'claude-gateway'
export type ManualVendor =
  | 'anthropic' | 'openai' | 'gemini' | 'local' | 'openai-compatible' | 'claude-gateway';

/** 저장 전 임시 자격증명으로 연결 테스트(저장 안 함). */
export async function testManualConnection(payload: {
  vendor: ManualVendor;
  base_url?: string;
  api_key?: string;
}): Promise<AiProviderHealthResult> {
  const { data } = await apiClient.post<AiProviderHealthResult>(
    '/admin/ai-providers/test-connection',
    payload,
    { timeout: 15_000 },
  );
  return data;
}

/** 연결 저장 — env 키를 ~/.claude/.env 에 머지 작성(자동 감지 반영). */
export async function connectManualProvider(
  keys: Record<string, string>,
): Promise<{ ok: boolean; written?: string[]; error?: string }> {
  const { data } = await apiClient.post<{ ok: boolean; written?: string[]; error?: string }>(
    '/admin/ai-providers/connect',
    { keys },
  );
  return data;
}

/** 연결 해제 — 해당 env 키 제거. */
export async function disconnectManualProvider(
  keys: string[],
): Promise<{ ok: boolean; removed?: string[]; error?: string }> {
  const { data } = await apiClient.post<{ ok: boolean; removed?: string[]; error?: string }>(
    '/admin/ai-providers/disconnect',
    { keys },
  );
  return data;
}


/* ── 판 H-② — AI 연결 목록 (시스템 관리자가 만들고 연다) ──────────────────
   한 항목이 «연결»(어디에 어떻게 붙나)과 «공개»(회사가 고를 수 있나)를 함께 담는다.
   ★ 토큰 원문은 **어떤 응답에도 안 온다** — 있음/없음과 꼬리 넉 자만 온다. */

export type AiSurface = 'ai-menu' | 'chat';
/**
 * 붙는 방식.
 *   `env` — 주소·토큰을 **이 항목이 갖는다**.
 *   `cli` — 이 서버의 실행 파일.
 *
 * ★ 판 J 에서 «가리키기(ref)»를 걷어냈다 — 관리 콘솔에서 주소도 토큰도 고칠 수 있어야 하는데,
 *   저쪽을 가리키면 여기서 못 고친다(2026-09-10 사용자 지시).
 */
export type AiEntryKind = 'env' | 'cli';

export interface AiEntry {
  /** 안정 식별자. **서버가 만든다** — 회사 선택이 이걸 가리킨다. */
  id: string;
  /** 회사 선택이 담는 값(`cat.<id>::<model>`). 서버가 만든다. */
  key: string;
  kind: AiEntryKind;
  label: string;
  /** env — API 주소. cli 면 빈 문자열. */
  baseUrl: string;
  /** cli — 실행 파일 이름. 그 밖이면 빈 문자열. */
  bin: string;
  model: string;
  surfaces: AiSurface[];
  enabled: boolean;
  /** 토큰은 원문이 안 온다 — 이 둘로만 안다. */
  hasApiKey: boolean;
  apiKeyHint: string | null;
}

/** 판 J — 연결별 사용량(최근 7일). **안 쓴 연결은 줄이 없다** — 「0」과 「기록 없음」은 다르다. */
export interface AiUsageRow {
  entryId: string;
  promptTokens: number;
  completionTokens: number;
  calls: number;
  todayTokens: number;
  lastDay: string | null;
  daily: Array<{ day: string; tokens: number; calls: number }>;
}

export interface AiCatalogResult {
  catalog: { entries: AiEntry[]; updated_at: string | null };
  surfaces: AiSurface[];
  kinds: AiEntryKind[];
  /** 최근 7일 사용량. 못 읽었으면 빈 배열이 온다. */
  usage?: AiUsageRow[];
  /** 이 서버에서 실제로 쓸 수 있는 것 — 적을 때 참고한다. */
  detected: {
    env: Array<{ id: string; name: string; base_url?: string }>;
    cli: Array<{ bin: string; name: string }>;
  };
}

/** 저장할 때 보내는 모양 — `apiKey` 는 **바꿀 때만** 넣는다(안 넣으면 서버가 예전 것을 지킨다). */
export interface AiEntryDraft {
  id?: string;
  kind: AiEntryKind;
  label: string;
  baseUrl?: string;
  apiKey?: string;
  bin?: string;
  model?: string;
  surfaces: AiSurface[];
  enabled: boolean;
}

export async function fetchAiCatalog(): Promise<AiCatalogResult> {
  const { data } = await apiClient.get<AiCatalogResult>('/admin/ai-providers/open-catalog');
  return data;
}

export async function saveAiCatalog(entries: AiEntryDraft[]): Promise<AiCatalogResult['catalog']> {
  const { data } = await apiClient.put<{ catalog: AiCatalogResult['catalog'] }>(
    '/admin/ai-providers/open-catalog',
    { entries },
  );
  return data.catalog;
}

/** 판 J — 이 서버가 이미 아는 provider 를 **값째** 가져온다(서버가 한다 — 토큰이 화면에 안 온다). */
export async function importDetectedProviders(): Promise<{ added: number; catalog?: AiCatalogResult['catalog'] }> {
  const { data } = await apiClient.post<{ ok: boolean; added: number; catalog?: AiCatalogResult['catalog'] }>(
    '/admin/ai-providers/open-catalog/import',
    {},
  );
  return { added: data.added, catalog: data.catalog };
}

/**
 * 판 J — 모델 목록을 물어본다.
 * ★ 저장된 항목이면 `entryId` 만 보낸다 — 토큰은 서버가 꺼내 쓴다(화면으로 안 내려온다).
 */
export async function probeAiModels(
  input: { entryId?: string; baseUrl?: string; apiKey?: string },
): Promise<{ ok: boolean; models?: string[]; error?: string }> {
  const { data } = await apiClient.post<{ ok: boolean; models?: string[]; error?: string }>(
    '/admin/ai-providers/probe-models',
    input,
  );
  return data;
}

/* ── 판 K — 사용량 집계 ────────────────────────────────────────────────────
   「총사용량, 회사별 사용량, 모델별」(2026-09-10 사용자 지시).
   ★ 넷을 한 번에 받는다 — 따로 물으면 그 사이에 값이 달라져 합계와 항목이 안 맞는다. */

export interface AiUsageBucket {
  key: string;
  label: string;
  promptTokens: number;
  completionTokens: number;
  calls: number;
}

/** 판 L — 묶는 단위. 「회사별, 년, 월, 주, 일 사용량과 모델별, 에이전트별」(사용자 지시). */
export type AiUsageUnit = 'day' | 'week' | 'month' | 'year';

export interface AiUsageRollup {
  days: number;
  from: string;
  unit: AiUsageUnit;
  total: { promptTokens: number; completionTokens: number; calls: number };
  byInstitution: AiUsageBucket[];
  byModel: AiUsageBucket[];
  byConnection: AiUsageBucket[];
  byAgent: AiUsageBucket[];
  byUser: AiUsageBucket[];
  byOrg: AiUsageBucket[];
  series: Array<{ bucket: string; tokens: number; calls: number }>;
}

export async function fetchAiUsageRollup(unit: AiUsageUnit = 'day'): Promise<AiUsageRollup> {
  const { data } = await apiClient.get<AiUsageRollup>('/admin/ai-providers/usage', { params: { unit } });
  return data;
}

/* ── 판 M — 회사별 토큰 한도 ───────────────────────────────────────────────
   「관리콘솔에서 시스템 관리자는 회사별 토큰량을 수정할 수 있다」(2026-09-10 사용자).
   ★★★ 넘어도 **막지 않는다** — 계량이 CLI 를 못 재기 때문. 화면이 알릴 뿐이다. */

export interface AiInstitutionQuota {
  institutionId: string;
  name: string;
  /** 한 달 한도. null = 무제한. */
  limit: string | null;
  /** 그 회사가 정한 사원 기본 — 보기만 한다(회사 관리자의 몫). */
  memberDefault: string | null;
  usedThisMonth: number;
  over: boolean;
  /** 무제한이면 null — 막대를 안 그린다. */
  percent: number | null;
}

export async function fetchAiQuota(): Promise<{ month: string; institutions: AiInstitutionQuota[] }> {
  const { data } = await apiClient.get<{ month: string; institutions: AiInstitutionQuota[] }>(
    '/admin/ai-providers/quota',
  );
  return data;
}

export async function saveAiQuota(institutionId: string, monthlyTokens: number | null): Promise<void> {
  await apiClient.put('/admin/ai-providers/quota', {
    institution_id: institutionId, monthly_tokens: monthlyTokens,
  });
}

/* ── 판 O — 시스템 설정 ────────────────────────────────────────────────────
   「관리 콘솔에 imap, smtp_host 를 설정할 수 있도록」(2026-09-10 사용자 지시).
   ★ 비밀(비밀번호)은 **원문이 안 온다** — 담겨 있나(`hasValue`)만 온다. */

export interface SystemSettingView {
  key: string;
  value?: string;
  hasValue?: boolean;
  secret: boolean;
  /** 표에 줄이 있나. `false` = 아직 **서버 파일(env)** 값이 답하고 있다. */
  fromTable: boolean;
}

export async function fetchSystemSettings(): Promise<SystemSettingView[]> {
  const { data } = await apiClient.get<{ settings: SystemSettingView[] }>('/admin/system-settings');
  return data.settings;
}

export async function saveSystemSettings(
  settings: Record<string, string>,
): Promise<SystemSettingView[]> {
  const { data } = await apiClient.put<{ settings: SystemSettingView[] }>(
    '/admin/system-settings', { settings },
  );
  return data.settings;
}

/** 실제로 붙어 본다. ★ 비밀번호를 안 보내면 **담아 둔 것**으로 시험한다. */
export async function testSystemMail(
  input: { host?: string; port?: string; secure?: string; user?: string; pass?: string },
): Promise<{ ok: boolean; message: string }> {
  const { data } = await apiClient.post<{ ok: boolean; message: string }>(
    '/admin/system-settings/test-mail', input,
  );
  return data;
}
